<template>
  <div>


<h2 class="goth">Genus och plural</h2>

<div class="grammatikparagrafbredd leftorient">

<p>I tyskan liksom svenskan (och de flesta indoeuropeiska språk) delas substantiv in i olika grammatiska genus (kön).
  Även om man i svenska i teorin pratar om 4 genus (maskulinum - han, femininum - hon, neutrum - det, utrum - den)
  finns det i modern svenska när det gäller substantiv i praktiken bara två – den-ord och det-ord (utrum och neutrum).</p>


<p>I tyskan däremot finns tre genus; der-ord (maskulinum), das-ord (neutrum) och die-ord (feminimun). Maskulinum och femininum
  är inte endast begränsat till levande varelser (med ”biologiskt kön”) utan även SAKER kan vara maskulinum och femininum
  (och levande varelser kan ibland vara neutrum). </p>


<p>Vilket genus ett ord har påverkar förutom den bestämda även obestämd artikel och andra artiklar till ordet,
  samt hur tillhörande adjektiv böjs, vilka pronomen som används m.m. Därför bör man vara noga med att lära sig
  rätt genus när man lär ett nytt tyskt substantiv. Detsamma gäller med varje substantivs pluraländelse. Tyskan
  liksom svenskan bildar plural på en mängd olika sätt. </p>

<p>Man kan <b>aldrig vara helt säker</b> på om ett tyskt substantiv är ett der-, das- eller die-ord och vilken plural det har
  genom att bara se på hur ordet ser ut eller vad det betyder. Men nedan kommer några av de <b>vanligaste
  reglerna</b> som kan ge en <b>fingervisning och stämmer i många fall</b>:</p>





<br>
<h4>der-ord</h4>
<div class="quasi-p"> Substantiv som betecknar personer av manligt kön
</div>


<div class="ruta">
<p>
    <i>der Mann <br>mannen</i>
</p>

<p>
    <i>der Bruder <br>brodern</i>
</p>

<p>
    <i>der Schauspieler <br>skådespelaren</i>
</p>

<p>
    <i>der Arzt <br>[manliga] läkaren</i>
</p>

</div>

<p class="mindre">Substantiv som betecknar yrken har i tyskan alltid en manlig och en kvinnlig version som är der- respektive
  die-ord</p>

<br><br>


  <div class="quasi-p"> Substantiv som slutar på <b>-er</b>
  </div>


  <div class="ruta">
  <p>
      <i>der Staubsauger <br>dammsugaren</i>
  </p>
  </div>
<br><br>



<div class="quasi-p"> Substantiv som slutar på <b>-us, -or</b>
</div>


<div class="ruta">
<p>
    <i>der Rhytmus <br>rytmen</i>
</p>

<p>
    <i>der Motor <br>motorn</i>
</p>


</div>
<br><br>



<div class="quasi-p"> Substantiv som slutar på <b>-ich, -ig</b>
</div>


<div class="ruta">
<p>
    <i>der Teppich <br>mattan</i>
</p>

<p>
    <i>der Käfig <br>buren</i>
</p>

</div>
<br><br>


<div class="quasi-p"> Substantiv som slutar på <b>-e</b> och betecknar nationalitet
</div>


<div class="ruta">
<p>
    <i>der Schwede <br>svensken</i>
</p>
<p>
    <i>der Däne <br>dansken</i>
</p>
</div>
<p class="mindre">Maskulina ord som slutar på <b>-e</b> utgör en egen grupp kallad <i>jungegruppen</i> och
  har en grammatik som i vissa lägen avviker från övriga substantiv. Men det behöver man oftast inte tänka på i början</p>


<h4>der-ord i plural</h4>
<p>De flesta der-ord ofta <b>-e</b> i plural och ofta omljud d.v.s. <i>a</i> blir <i>ä</i>, <i>u</i> blir <i>ü</i>,
<i>o</i> blir <i>ö</i> och <i>au</i> blir <i>äu</i>.</p>
<p>Men der-ord (och das-ord) som slutar på <b>-en, -el</b> och <b>-er</b> får ingen plural ändelse (men kan ändå få omljud)</p>
<p>Lånord får ofta <b>-s</b> i plural</p>






<br>
<h4>das-ord</h4>
<div class="quasi-p"> Substantiv som betecknar barn / ungar till människor / djur
</div>


<div class="ruta">
<p>
    <i>das Kind <br>barnet</i>
</p>

<p>
    <i>das Kalb <br>kalven</i>
</p>

</div>

<br><br>


  <div class="quasi-p"> Gamla ord som är besläktade med svenskan morsvarighet och är neutrum i svenskan
  </div>


  <div class="ruta">
  <p>
      <i>das Wasser<br>vattnet</i>
  </p>
  <p>
      <i>das Haus<br>huset</i>
  </p>
  </div>
<br><br>



<div class="quasi-p"> Substantiv som slutar på <b>-lein, -chen</b>
</div>


<div class="ruta">
  <p>
      <i>das Kaninchen <br>kaninen</i>
  </p>


<p>
    <i>das Freulein <br>fröken</i>
</p>

<p>
    <i>das Mädchen <br>flickan</i>
</p>


</div>
<p class="mindre">Observera att denna regel som baseras på ordets form ”trumfar” regeln
  om biologiskt kön - de två sista exemplen betecknar ju båda levande varelser av kvinnligt kön men
  är ändå das-ord.</p>
<br><br>



<div class="quasi-p"> Substantiv som slutar på <b>-en</b>. De är bildade av infinitivformen av motsvarande verb
</div>


<div class="ruta">
<p>
    <i>das Essen <br>maten</i>

</p>

<p>
    <i>das Treffen <br>mötet / träffen</i>

</p>
</div>
<br><br>


<div class="quasi-p"> Substantiv som slutar på <b>-nis, -um, -ment</b>
</div>


<div class="ruta">
<p>
    <i>das Erlebnis <br>upplevelsen</i>
</p>
<p>
    <i>das Museum<br>museet</i>
</p>

<p>
    <i>das Instrument<br>instrumentet</i>
</p>
</div>

<br>
<h4>das-ord i plural</h4>
<p>das-ord ofta <b>-e</b> i plural. </p>
<p>Många korta das-ord får <b> -er </b> och omljud i plural.
</p>
<p>das-ord (och der-ord) som slutar på <b>-en, -el</b> och <b>-er</b> får ingen plural ändelse (men kan ändå få omljud)</p>
<p>Lånord får ofta <b>-s</b> i plural</p>
<br>

<h4>die-ord</h4>
<div class="quasi-p"> Substantiv som betecknar personer av kvinnligt kön
</div>


<div class="ruta">
<p>
    <i>die Frau <br>kvinnan</i>
</p>

<p>
    <i>die Tochter <br>dottern</i>
</p>

<p>
    <i>die Schauspielerin <br>skådespelerskan</i>
</p>

</div>

<br><br>


<div class="quasi-p"> Substantiv som slutar på <b>-e</b>
</div>


<div class="ruta">
  <p>
      <i>die Blume <br>blomman</i>
  </p>


<p>
    <i>die Schule <br>skolan</i>
</p>

<p>
    <i>die Woche <br>veckan</i>
</p>


</div>
<br><br>



<div class="quasi-p"> Substantiv som slutar på <b>-ei, heit,- keit, -schaft, -ung</b>
</div>


<div class="ruta">
<p>
    <i>die Partei <br>partiet</i>
</p>
<p>
    <i>die Freiheit <br>friheten</i>
</p>
<p>
    <i>die Arbeitslosigkeit <br>arbetslösheten</i>
</p>
<p>
    <i>die Gesellschaft <br>sällskapet / sammhället</i>
</p>
<p>
    <i>die Zeitung <br>tidningen</i>
</p>



</div>
<br><br>


<div class="quasi-p"> Substantiv som slutar på <b>-ie, -ik, -ion, -tät, </b>
</div>


<div class="ruta">
<p>
    <i>die Idiologie <br>idiologin</i>
</p>
<p>
    <i>die Grammatik<br>grammatiken</i>
</p>

<p>
    <i>die Information<br>informationen</i>
</p>

<p>
    <i>die Universität<br>universitetet</i>
</p>

<p>
    <i>die Natur<br>naturen</i>
</p>

</div>
<p class="mindre">Observera det viktiga undantaget till den sista regeln: <i>ta studenten</i> heter
  <i><b>das </b> Abitur machen</i>
  </p>
<br><br>

<div class="quasi-p"> Substantiv som slutar på bildas av verbstam <b>+t</b> eller <b>+d</b>
</div>


<div class="ruta">
<p>
    <i>die Fahrt <br>färden</i>
</p>
<p>
    <i>die Jagd <br>jakten</i>
</p>
</div>
<br>
<h4>die-ord i plural</h4>
<p>die-ord som slutar på <b>-e</b> får <b>-n</b> i plural</p>

<p>die-ord som slutar på <b>-in</b> får <b>-nen</b> i plural</p>

<p>die-ord som slutar på <b>-heit, -ung </b> m.fl. får <b>-en</b> i plural</p>

<p>Kort sagt slutar de flesta feminina ord i plural på <b>-n</b> (även om det inte alltid är HELA pluraländelsen)</p>

<br>






</div>

</div>





</template>

<script>
export default {
  name: 'Grammatik',
  props: {
    msg: String
},
metaInfo() {
    return {
        meta: [
            {
                vmid: "description",
                name: "description",
                content: "ob wenn om huruvida"
            }
        ]
    }
},

}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>

</style>
